// @flow
import {
  AdSlot,
  Grid,
  GridItem,
  Query,
  SeoData,
  SortableTable,
  StaticTable,
  TableGraphConnector,
} from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { StyleProps, } from '@haaretz/htz-css-tools';
import type { Node, } from 'react';

import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import ExchangeQuery from '../queries/exchangeQuery.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const numToString: (number | string) => string = num => (typeof num === 'number'
  ? num.toLocaleString('he', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  : num);

function stock({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title="מניות - TheMarker Finance"
        description="כל המידע על  מניות: נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance"
        path={asPath}
      />
      <FelaTheme>
        {theme => (
          <Fragment>
            <PageRow miscStyles={{ marginBottom: [ { from: 'l', value: '0', }, ], }}>
              <RowItem title="מדדים">
                <FelaComponent
                  style={() => ({
                    extend: [ theme.mq({ from: 'l', }, { display: 'none', }), ],
                  })}
                >
                  <Query
                    query={ExchangeQuery}
                    variables={{
                      ids: [ '2', '142', '137', '147', '164', '143', '167', '145', '149', ],
                    }}
                  >
                    {({ loading, error, data, }) => {
                      if (loading || error) return null;
                      const { assets = [], } = data || {};
                      return (
                        <StaticTable
                          data={assets}
                          linkContent
                          columns={[
                            {
                              title: 'שם נייר',
                              name: 'name',
                              styles: {
                                fontWeight: '700',
                                maxWidth: '17rem',
                                paddingStart: '2rem',
                              },
                              headerStyle: { width: '45%', },
                              render: value => value,
                            },
                            {
                              title: 'שער אחרון',
                              name: 'value',
                              styles: {
                                paddingStart: '2rem',
                              },
                              render: value => (value ? numToString(value) : '-'),
                            },
                            {
                              title: '% שינוי',
                              percentage: true,
                              name: 'changePercentage',
                              styles: function styles(value: number): StyleProps {
                                return {
                                  ...(value
                                    ? {
                                      color:
                                          value < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                    }
                                    : {}),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingStart: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                };
                              },
                              render: value => `
                                  ${value > 0 ? '' : value < 0 ? '-' : ''}
                                  ${value ? `${numToString(Math.abs(value))}%` : '-'}
                              `,
                            },
                          ]}
                        />
                      );
                    }}
                  </Query>
                </FelaComponent>
                <TableGraphConnector
                  miscStyles={{ display: [ { until: 'l', value: 'none', }, ], }}
                  assetsId={[ '2', '142', '137', '147', '164', '143', '167', '145', '149', ]}
                />
              </RowItem>
            </PageRow>
            <PageRow
              miscStyles={{
                paddingTop: '4rem',
                paddingBottom: '4rem',
                backgroundColor: theme.color('neutral', '-10'),
              }}
            >
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow>
              <Grid>
                <GridItem
                  width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 2, }, ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="המניות העולות - תל אביב">
                    <SortableTable
                      queryPrefix="TA_Up"
                      filters={[ { section: 'index', subSection: '2', }, ]}
                      type="stock"
                      fragment="
                        name
                        value
                        changePercentage
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: '45%', },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'value',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          value: ({ value, }) => (value ? numToString(value) : '-'),
                        },
                        {
                          name: 'changePercentage',
                          display: '% שינוי',
                          sortingOrder: 'desc',
                          style: ({ changePercentage, }) => ({
                            ...(changePercentage
                              ? {
                                color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ changePercentage, }) => `
                            ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                            ${
                              changePercentage
                                ? numToString(Math.abs(changePercentage))
                                : changePercentage
                            }%
                          `,
                        },
                      ]}
                      initialSort="changePercentage"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 2, }, ]}>
                  <RowItem title="המניות העולות - נאסד״ק">
                    <SortableTable
                      queryPrefix="Nasdaq_Up"
                      filters={[ { section: 'index', subSection: '29.10.@CCO', }, ]}
                      type="stock"
                      fragment="
                        name
                        USDValue
                        changePercentage
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: '45%', },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'USDValue',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          value: ({ USDValue, }) => (USDValue ? numToString(USDValue) : '-'),
                        },
                        {
                          name: 'changePercentage',
                          display: '% שינוי',
                          sortingOrder: 'desc',
                          style: ({ changePercentage, }) => ({
                            ...(changePercentage
                              ? {
                                color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ changePercentage, }) => `
                            ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                            ${
                              changePercentage
                                ? numToString(Math.abs(changePercentage))
                                : changePercentage
                            }%
                          `,
                        },
                      ]}
                      initialSort="changePercentage"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow>
              <Grid>
                <GridItem
                  width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 2, }, ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="המניות היורדות - תל אביב">
                    <SortableTable
                      queryPrefix="TA_Down"
                      filters={[ { section: 'index', subSection: '2', }, ]}
                      type="stock"
                      fragment="
                        name
                        value
                        changePercentage
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: '45%', },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'value',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          value: ({ value, }) => (value ? numToString(value) : '-'),
                        },
                        {
                          name: 'changePercentage',
                          display: '% שינוי',
                          sortingOrder: 'asc',
                          style: ({ changePercentage, }) => ({
                            ...(changePercentage
                              ? {
                                color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ changePercentage, }) => `
                            ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                            ${
                              changePercentage
                                ? numToString(Math.abs(changePercentage))
                                : changePercentage
                            }%
                          `,
                        },
                      ]}
                      initialSort="changePercentage"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 2, }, ]}>
                  <RowItem title="המניות היורדות - נאסד״ק">
                    <SortableTable
                      queryPrefix="Nasdaq_Down"
                      filters={[ { section: 'index', subSection: '29.10.@CCO', }, ]}
                      type="stock"
                      fragment="
                        name
                        USDValue
                        changePercentage
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: '45%', },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'USDValue',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          value: ({ USDValue, }) => (USDValue ? numToString(USDValue) : '-'),
                        },
                        {
                          name: 'changePercentage',
                          display: '% שינוי',
                          sortingOrder: 'asc',
                          style: ({ changePercentage, }) => ({
                            ...(changePercentage
                              ? {
                                color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ changePercentage, }) => `
                            ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                            ${
                              changePercentage
                                ? numToString(Math.abs(changePercentage))
                                : changePercentage
                            }%
                          `,
                        },
                      ]}
                      initialSort="changePercentage"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow>
              <Grid>
                <GridItem
                  width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 2, }, ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="המניות הפעילות - תל אביב">
                    <SortableTable
                      queryPrefix="TA_Active"
                      filters={[
                        { section: 'index', subSection: '2', },
                        { section: 'type', subSection: 'stock', },
                      ]}
                      type="stock"
                      fragment="
                        name
                        value
                        volume
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: [ { until: 'l', value: '45%', }, ], },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'value',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          value: ({ value, }) => (value ? numToString(value) : '-'),
                        },
                        {
                          name: 'volume',
                          display: 'מחזור',
                          sortingOrder: 'desc',
                          value: ({ volume, }) => (volume ? numToString(volume) : '-'),
                        },
                      ]}
                      initialSort="volume"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 2, }, ]}>
                  <RowItem title="המניות הפעילות - נאסד״ק">
                    <SortableTable
                      queryPrefix="Nasdaq_Active"
                      filters={[ { section: 'index', subSection: '29.10.@CCO', }, ]}
                      type="stock"
                      fragment="
                        name
                        USDValue
                        volume
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: [ { until: 'l', value: '45%', }, ], },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'USDValue',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          value: ({ USDValue, }) => (USDValue ? numToString(USDValue) : '-'),
                        },
                        {
                          name: 'volume',
                          display: 'מחזור',
                          sortingOrder: 'desc',
                          value: ({ volume, }) => (volume ? numToString(volume) : '-'),
                        },
                      ]}
                      initialSort="volume"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow>
              <Grid gutter={2}>
                <GridItem
                  width={[ { until: 'l', value: 1, }, { from: 'l', value: 4 / 5, }, ]}
                  miscStyles={{
                    order: [ { until: 'l', value: '2', }, ],
                  }}
                >
                  <RowItem title="פערי ארביטראז׳">
                    <SortableTable
                      queryPrefix="Arbitrage"
                      filters={[ { section: 'index', subSection: '-2000', }, ]}
                      type="stock"
                      loadMore
                      fragment="
                        name
                        dualValue
                        symbol
                        USDValue
                        arbGap
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם נייר',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          headerStyle: { width: '27%', },
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'dualValue',
                          display: 'שער אחרון',
                          sortingOrder: 'desc',
                          hideUntil: [ 's', ],
                          value: ({ dualValue, }) => (dualValue ? numToString(dualValue) : '-'),
                        },
                        {
                          name: 'symbol',
                          display: 'סימול בוול סטריט',
                          sortingOrder: 'asc',
                          headerStyle: { width: [ { from: 'xl', value: '20%', }, ], },
                          value: ({ symbol, }) => symbol,
                        },
                        {
                          name: 'USDValue',
                          display: 'שער בדולרים',
                          sortingOrder: 'desc',
                          hideUntil: [ 's', ],
                          value: ({ USDValue, }) => (USDValue ? numToString(USDValue) : '-'),
                        },
                        {
                          name: 'arbGap',
                          display: "פער ארביטראז'",
                          sortingOrder: 'desc',
                          style: (arbGap: number) => ({
                            ...(arbGap
                              ? {
                                color:
                                    arbGap < 0 ? theme.color('negative') : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ arbGap, }) => (arbGap ? `${numToString(arbGap)}%` : '-'),
                        },
                      ]}
                      initialSort="name"
                      count={13}
                    />
                  </RowItem>
                </GridItem>
                <GridItem
                  width={[ { until: 'l', value: 1, }, { from: 'l', value: 1 / 5, }, ]}
                  miscStyles={{
                    alignSelf: 'center',
                    order: [ { until: 'l', value: '1', }, ],
                    marginBottom: [ { until: 'l', value: '4rem', }, ],
                  }}
                >
                  <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner3'], adUnitPathTemplate)} />
                </GridItem>
              </Grid>
            </PageRow>
          </Fragment>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default stock;
